import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { AoIconUrlPipe } from "~app/common/pipes/ao-icon-url.pipe";
import { EventService } from "~app/common/services/cqs/event.service";
import { ArbetsorderMaskinlagUpdatedMessage } from "~app/features/planera/events/ArbetsorderMaskinlagUpdatedMessage";
import { ArbetsorderOperationDeletedEvent } from "~app/features/planera/events/ArbetsorderOperationDeletedEvent";
import { ArbetsorderOperationUpdatedEvent } from "~app/features/planera/events/ArbetsorderOperationUpdatedEvent";
import { IArbetsorder } from "~app/features/planera/model/IArbetsorder";
import { ArbetsorderKartSymbolService } from "../../../features/planera/services/arbetsorder-kart-symbol.service";

@Component({
    selector: "rask-arbetsorder-info-icon",
    templateUrl: "./arbetsorder-info-icon.component.html",
    styleUrls: ["./arbetsorder-info-icon.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AoIconUrlPipe]
})
export class ArbetsorderInfoIconComponent implements OnInit, OnChanges, OnDestroy {
    @Input() arbetsorder: IArbetsorder;
    circleRadius: number;
    @Input() forceImageUrl = "";
    iconText: string;
    imgSrc: string;
    squareRoundness: number;
    squareSize: number;
    svgPath: string;
    svgShape: string;
    svgStyle: string;
    svgXPos = 0;
    @Input() useImage = false;

    private readonly arbetsorderKartSymbolService = inject(ArbetsorderKartSymbolService);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    ngOnChanges(changes: SimpleChanges) {
        if (changes.arbetsorder && changes.arbetsorder.currentValue !== undefined) {
            this.generateInfoIcon();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.generateInfoIcon();

        this.eventService
            .subscribeEvent<ArbetsorderMaskinlagUpdatedMessage>(
                ArbetsorderMaskinlagUpdatedMessage.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe(() => {
                this.generateInfoIcon();
            });

        this.eventService
            .subscribeEvent<ArbetsorderOperationUpdatedEvent>(
                ArbetsorderOperationUpdatedEvent.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe(() => {
                this.generateInfoIcon();
            });

        this.eventService
            .subscribeEvent<ArbetsorderOperationDeletedEvent>(
                ArbetsorderOperationDeletedEvent.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe(() => {
                this.generateInfoIcon();
            });
    }

    private generateInfoIcon() {
        if (this.forceImageUrl) {
            this.useImage = true;
            this.imgSrc = this.forceImageUrl;
            return;
        }
        const infoIcon = this.arbetsorderKartSymbolService.getIconInfoForArbetsorder(this.arbetsorder);
        if (this.useImage) {
            this.imgSrc = infoIcon.getImgSrc();
        }
        if (infoIcon.shape === "CIRCLE") {
            this.svgShape = "CIRCLE";
            this.circleRadius = 11;
            this.svgXPos = 4;
        } else if (infoIcon.shape === "TRIANGLE") {
            this.svgPath = infoIcon.svgPath;
            this.svgShape = "PATH";
        } else if (infoIcon.shape === "SQUARE") {
            this.svgShape = "RECT";
            this.squareRoundness = 3;
            this.squareSize = 21;
            this.svgXPos = 5;
        }
        this.iconText = infoIcon.iconText;
        this.svgStyle = `fill: ${infoIcon.iconFillColor};`;
        if (infoIcon.iconFillColor === "#FFFFFF") {
            this.svgStyle = `${this.svgStyle} stroke: #bbb; stroke-width: 1px;`;
        }
    }
}
